import { useState, useCallback } from "react";

const STEPS = ["87.5%", "93.75%", "100%"] as const;
type ZoomStep = (typeof STEPS)[number];
const STORAGE_KEY = "qt-zoom";

function getStoredStep(): ZoomStep | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && (STEPS as readonly string[]).includes(stored)) {
    return stored as ZoomStep;
  }
  return null;
}

function getInitialStep(): ZoomStep {
  return getStoredStep() ?? "100%";
}

/**
 * Reapplies the saved zoom when the app starts. A saved 100% still has to be
 * set, because index.css shrinks the root font size on smaller screens.
 */
export function restoreZoom(): void {
  try {
    const stored = getStoredStep();
    if (stored) document.documentElement.style.fontSize = stored;
  } catch {
    // Storage can be blocked; the app still works at the default size.
  }
}

function applyZoom(value: ZoomStep) {
  document.documentElement.style.fontSize = value;
  localStorage.setItem(STORAGE_KEY, value);
}

export function useZoom() {
  const [current, setCurrent] = useState<ZoomStep>(getInitialStep);

  const zoomOut = useCallback(() => {
    setCurrent((prev) => {
      const idx = STEPS.indexOf(prev);
      if (idx <= 0) return prev;
      const next = STEPS[idx - 1];
      applyZoom(next);
      return next;
    });
  }, []);

  const zoomIn = useCallback(() => {
    setCurrent((prev) => {
      const idx = STEPS.indexOf(prev);
      if (idx >= STEPS.length - 1) return prev;
      const next = STEPS[idx + 1];
      applyZoom(next);
      return next;
    });
  }, []);

  const isMin = current === STEPS[0];
  const isMax = current === STEPS[STEPS.length - 1];

  return { current, zoomIn, zoomOut, isMin, isMax };
}
